'use client';

import useSWR, { mutate } from 'swr';
import FilterBuilder from '../utils/filterBuilder';
import { fleetApi } from '../api/axiosClient';
import { useMemo } from 'react';

const getTzParam = () => `?tz=${Math.abs(new Date().getTimezoneOffset() / 60)}`;

const fetcher = async ([url, payload]) => {
  const response = await fleetApi.post(url + getTzParam(), payload);
  return response;
};

export const revalidateTransitPointList = () => {
  mutate((key) => Array.isArray(key) && key[0] === '/transit-point/search');
};

export const useTransitPointList = (page, rowsPerPage, searchKey, searchValue, companyId = '') => {
  const start = page * rowsPerPage;

  const tableName = searchKey === 'customer' ? 'm_customer' : null;
  const filterLike = searchKey === 'customer' ? 'name' : searchKey;

  const payload = useMemo(() => {
    const fb = new FilterBuilder().setPaging(start, rowsPerPage);

    if (searchValue && searchValue.trim() !== '') {
      fb.like(filterLike, searchValue, 'AND', tableName);
    }

    fb.addJoin('company', ['name']).addJoin('customer', ['name']).setOrder(['name'], true);
    if (companyId) {
      const idStr = String(companyId);
      if (idStr.includes(',')) {
        fb.in('company_id', idStr);
      } else {
        fb.equal('company_id', idStr);
      }
    } else {
      fb.filterByCompany();
    }

    return fb.build();
  }, [start, rowsPerPage, searchValue, companyId, filterLike, tableName]);
  const key = useMemo(() => ['/transit-point/search', JSON.stringify(payload)], [payload]);
  const { data, error, isLoading } = useSWR(key, fetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    shouldRetryOnError: false,
    keepPreviousData: true,
    revalidateOnMount: true
  });

  return {
    data: data?.data,
    totalRecord: data?.totalRecord,
    error,
    isLoading
  };
};

export const getTransitPointById = async (id) => {
  if (id == null) throw new Error('id is required');
  try {
    const resp = await fleetApi.get(`/transit-point/${id}${getTzParam()}`);
    if (resp?.data) return resp.data;

    const fb = new FilterBuilder().setPaging(0, 1).setOrder(['id'], false).filterByCompany().equal('id', String(id));

    const payload = fb.build();
    const response = await fleetApi.post(`/transit-point/search${getTzParam()}`, payload);
    const dataArr = response?.data?.data ?? response?.data ?? (Array.isArray(response) ? response : null);
    return Array.isArray(dataArr) && dataArr.length > 0 ? dataArr[0] : null;
  } catch (err) {
    console.error('Failed to fetch transit point by id:', err);
    throw err;
  }
};

export const createTransitPoint = async (formData = {}) => {
  try {
    const body = {
      ...formData
    };

    const response = await fleetApi.post(`/transit-point/new${getTzParam()}&withoutCust=true`, body);

    if (response?.code >= 400 || response?.status === false || response?.error === true) {
      throw { response: { data: response } };
    }
    revalidateTransitPointList();
    return response.data;
  } catch (err) {
    console.error('Error creating transit point:', err);
    throw err;
  }
};

export const updateTransitPoint = async (id, formData = {}) => {
  if (id == null) throw new Error('id is required');
  try {
    const body = {
      ...formData
    };

    const response = await fleetApi.put(`/transit-point/edit/${id}${getTzParam()}&withoutCust=true`, body);

    if (response?.code >= 400 || response?.status === false || response?.error === true) {
      throw { response: { data: response } };
    }
    revalidateTransitPointList();
    mutate(`/transit-point/${id}`);
    return response.data;
  } catch (err) {
    console.error('Error updating transit point:', err);
    throw err;
  }
};

export const deleteTransitPoint = async (id) => {
  if (id == null) throw new Error('id is required');
  try {
    const response = await fleetApi.delete(`/transit-point/delete/${id}${getTzParam()}`);

    if (response?.code >= 400 || response?.status === false || response?.error === true) {
      throw { response: { data: response } };
    }
    revalidateTransitPointList();
    return response.data;
  } catch (err) {
    console.error('Error deleting transit point:', err);
    throw err;
  }
};

export const updateGeomTransitPoint = async (id, geomData = {}) => {
  if (id == null) throw new Error('id is required');
  try {
    const body = {
      ...geomData
    };
    const response = await fleetApi.put(`/transit-point/edit/${id}${getTzParam()}&withoutCust=true`, body);

    if (response?.code >= 400 || response?.status === false || response?.error === true) {
      throw { response: { data: response } };
    }
    revalidateTransitPointList();
    return response.data;
  } catch (error) {
    console.error('Error updating transit-point geometry:', error?.response?.data ?? error);
    throw error;
  }
};
